/**
 * Every navigable page, flattened for jump-to-page search.
 *
 * The sidebar keeps its own nested structure because it needs icons, badges,
 * and collapse state. This catalog is the flat projection of the same routes,
 * kept deliberately plain so the quick-search stays a pure string match.
 * `navCatalog.test.ts` asserts every path here is a real route in App.tsx.
 */
export type NavPage = {
  label: string;
  path: string;
  /** The sidebar group this page lives under, shown as search context. */
  group: string;
};

export const NAV_PAGES: NavPage[] = [
  { label: 'Dashboard', path: '/', group: 'Overview' },

  { label: 'Domain Whitelist', path: '/domains', group: 'Domains' },
  { label: 'Domain Directory', path: '/domain-directory', group: 'Domains' },
  { label: 'Domain Processing', path: '/domain-processing', group: 'Domains' },
  { label: 'TCPA Languages', path: '/languages', group: 'Domains' },

  { label: 'ATBS Review queue', path: '/atbs', group: 'Calls & QA' },
  { label: 'Customer signals', path: '/customer-signals', group: 'Calls & QA' },
  { label: 'Customer insights', path: '/customer-signals/insights', group: 'Calls & QA' },
  { label: 'ATBS Campaign pixels', path: '/atbs/campaign-pixels', group: 'Calls & QA' },
  { label: 'ATBS Documentation', path: '/atbs/documentation', group: 'Calls & QA' },
  { label: 'Historical ReAudit Overview', path: '/historical-reaudit', group: 'Calls & QA' },
  { label: 'Historical Call Explorer', path: '/historical-reaudit/calls', group: 'Calls & QA' },
  { label: 'Historical Campaign Analytics', path: '/historical-reaudit/campaigns', group: 'Calls & QA' },
  { label: 'Historical Themes & Phrases', path: '/historical-reaudit/themes', group: 'Calls & QA' },
  { label: 'Historical Review Queue', path: '/historical-reaudit/review', group: 'Calls & QA' },
  { label: 'Historical Runs, Errors & Spend', path: '/historical-reaudit/runs', group: 'Calls & QA' },
  { label: 'Call Lookup', path: '/call-lookup', group: 'Calls & QA' },
  { label: 'Ringba Campaigns', path: '/ringba/campaigns', group: 'Calls & QA' },
  { label: 'Ringba Call flows', path: '/ringba/topology', group: 'Calls & QA' },
  { label: 'Ringba Performance & Alerts', path: '/ringba/performance', group: 'Calls & QA' },
  { label: 'Ringba ATBS coverage', path: '/ringba/atbs', group: 'Calls & QA' },
  { label: 'Ringba Templates', path: '/ringba/templates', group: 'Calls & QA' },
  { label: 'Walker Fetch by ID', path: '/walker-transcripts', group: 'Calls & QA' },
  { label: 'Walker Search', path: '/walker-transcripts/search', group: 'Calls & QA' },
  { label: 'Walker Insights', path: '/walker-transcripts/insights', group: 'Calls & QA' },
  { label: 'Walker Runs', path: '/walker-transcripts/runs', group: 'Calls & QA' },
  { label: 'Walker Usage & cost', path: '/walker-transcripts/usage', group: 'Calls & QA' },
  { label: 'Walker Gold set', path: '/walker-transcripts/gold-set', group: 'Calls & QA' },
  { label: 'Walker settings', path: '/walker-transcripts/settings', group: 'Calls & QA' },

  { label: 'Webhook Data', path: '/webhook-data', group: 'Leads & Data' },
  { label: 'IPQS Daily Overview', path: '/ipqs', group: 'Leads & Data' },
  { label: 'IPQS Insights Table', path: '/ipqs/insights', group: 'Leads & Data' },
  { label: 'IPQS Submission IP Report', path: '/ipqs/submissions', group: 'Leads & Data' },
  { label: 'IPQS Single Check', path: '/ipqs/check', group: 'Leads & Data' },
  { label: 'TrustedForm Deep Insights', path: '/trustedform', group: 'Leads & Data' },
  { label: 'Dedupe API', path: '/dedupe-api', group: 'Leads & Data' },
  { label: 'Meera SMS Leads', path: '/masstort-coreg', group: 'Leads & Data' },
  { label: 'Meera Lead Manager', path: '/meera-lead-manager', group: 'Leads & Data' },

  { label: 'WA_ID coverage', path: '/ad-compliance', group: 'Advertising' },
  { label: 'Monitored libraries', path: '/ad-compliance/libraries', group: 'Advertising' },
  { label: 'IntegriShield', path: '/integrishield', group: 'Advertising' },

  { label: 'Spend Monitor', path: '/spend-monitor', group: 'Operations' },
  { label: 'Resource Hub', path: '/hub', group: 'Operations' },
  { label: 'Bugs & Feature Requests', path: '/feedback', group: 'Operations' },
  { label: 'Settings', path: '/settings', group: 'Operations' },

  { label: 'Ping/Post Analysis', path: '/pingpost', group: 'Work in progress' },
  { label: 'AI Call Insights', path: '/call-insights', group: 'Work in progress' },
  { label: 'Lead Documents', path: '/leads', group: 'Work in progress' },
  { label: 'Filter Management', path: '/filter-management', group: 'Work in progress' },
  { label: 'Keyword Watch', path: '/keyword-watch', group: 'Work in progress' },
  { label: 'Language Monitor', path: '/tcpa-language-monitor', group: 'Work in progress' },
  { label: 'TCPA Reviews', path: '/tcpa-reviews', group: 'Work in progress' },
  { label: 'Legacy Dashboard', path: '/legacy-dashboard', group: 'Work in progress' },

  { label: 'Copy Paste', path: '/copy-paste', group: 'Idea drafts' },
];

/**
 * Ranks pages against a query. Prefix matches beat interior matches, so typing
 * "dom" puts Domain Whitelist above Meera Lead Manager's "dom" in "random".
 */
export function searchNavPages(query: string, pages: NavPage[] = NAV_PAGES): NavPage[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  return pages
    .map((page) => {
      const label = page.label.toLowerCase();
      const index = label.indexOf(needle);
      if (index === 0) return { page, rank: 0 };
      if (index > 0) return { page, rank: 1 };
      if (page.path.toLowerCase().includes(needle)) return { page, rank: 2 };
      if (page.group.toLowerCase().includes(needle)) return { page, rank: 3 };
      return null;
    })
    .filter((hit): hit is { page: NavPage; rank: number } => hit !== null)
    .sort((a, b) => a.rank - b.rank || a.page.label.localeCompare(b.page.label))
    .map((hit) => hit.page);
}
